import React, { useState } from 'react';
import classNames from 'classnames';
import { FORM_ERROR, FormApi } from 'final-form';
import { useFormState } from 'react-final-form';
import { Prompt } from 'react-router-dom';
import { Button } from '@tcl-boron-prefabs/button';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import {
  FINANCIAL_ASSISTANCE_FORM_V4_KEY,
  FinancialAssistanceCalculationStatus,
  RELATIONSHIP_TO_PATIENT_NAME_V4,
} from '@tempus/patient-forms-service-shared';
import { NavigationFormProps } from '../../../custom-to-do-list/components/NavigatorChecklist';
import { FinancialAidDto, FormProps, submitFormGeneric } from '../../forms';
import Form from '../../shared/Form';
import FormAlert, { AlertStatus } from '../../shared/FormAlert';
import FormBannerError from '../../shared/FormBannerError';
import { FormTableField } from '../../shared/FormTable';
import Logo from '../../shared/Logo';
import { StatusBadgeProps } from '../../shared/StatusBadge';
import {
  LEAVE_FORM_WARNING,
  deleteFalsyStringFields,
  deleteUnmetConditionalFields,
  setReadOnlyValidatedValuesOnSubmit,
} from '../../shared/utils';
import ConsentToApplicationV4Table, { consentToApplicationV4Fields } from './ConsentToApplicationV4Table';
import ExtenuatingCircumstancesV4Table, { extenuatingCircumstancesV4Fields } from './ExtenuatingCircumstancesV4Table';
import OrderingPhysicianV4Table, { orderingPhysicianV4Fields } from './OrderingPhysicianV4Table';
import PatientInformationV4Table, { patientInformationV4Fields } from './PatientInformationV4Table';

import styles from '../../shared/styles.module.css';
import { useQuery } from '../../../../api/hooks';
import { FAOncoNeuroSelector } from '../shared/OncoNeuroSelector';
import { PatientFormsTitleInjection } from '../../../custom-to-do-list/components/PatientFormsTitleInjection';
import { getOktaAccessToken } from '../../../../api/Okta';
import { requiresInternalAuthentication } from '../shared/utils/requiresInternalAuthentication';

export const FINANCIAL_ASSISTANCE_V4_KEY = FINANCIAL_ASSISTANCE_FORM_V4_KEY;

type FinancialAidV4FormProps = FormProps & {
  statement: string;
  status: FinancialAssistanceCalculationStatus;
  title: string;
};

const FinancialAssistanceV4: React.FunctionComponent<FinancialAidV4FormProps> = (props) => {
  const formState = useFormState();

  return (
    <div>
      {props.handleNavigationBack && (
        <Prompt when={formState.dirty && !formState.submitSucceeded} message={LEAVE_FORM_WARNING} />
      )}
      <div className={classNames(typography.body, styles.screen)}>
        <div className={styles.header}>
          <div className={styles.headerLogo}>
            <Logo />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.title}>Financial Assistance</div>
          {formState.submitFailed ? <FormBannerError /> : undefined}
          {!props.handleNavigationSubmit ? (
            props.showAlert ? (
              <FormAlert
                setShowAlert={props.setShowAlert}
                message={props.statement}
                title={props.title}
                status={
                  props.status === FinancialAssistanceCalculationStatus.APPROVED
                    ? AlertStatus.SUCCESS
                    : AlertStatus.WARNING
                }
              />
            ) : undefined
          ) : undefined}
          <div className={styles.subtitle}>Please complete all fields</div>
          <PatientInformationV4Table validatedValues={props.validatedValues} />
          <OrderingPhysicianV4Table />
          <ExtenuatingCircumstancesV4Table />
          <ConsentToApplicationV4Table />
          <div className={styles.footer}>
            {props.handleNavigationBack && (
              <div className={styles.backButton}>
                <Button
                  ariaLabel="financial-assistance-back"
                  type="button"
                  buttonType="secondary"
                  onClick={props.handleNavigationBack}
                  disabled={formState.submitting}
                >
                  Back
                </Button>
              </div>
            )}
            <Button loading={formState.submitting} ariaLabel="financial-assistance-submit" type="submit">
              Submit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getAllFinancialAssistanceV4TableFormFields = (): FormTableField[] => {
  return patientInformationV4Fields.concat(
    orderingPhysicianV4Fields,
    consentToApplicationV4Fields,
    extenuatingCircumstancesV4Fields,
  );
};

const FinancialAssistanceV4Wrapper: React.FunctionComponent<NavigationFormProps> = (props) => {
  const selectedId = props.handleNavigationSubmit ? useQuery('orderId') : null;
  const [financialAidStatus, setFinancialAidStatus] = useState<FinancialAssistanceCalculationStatus>(
    FinancialAssistanceCalculationStatus.DENIED,
  );
  const [financialAidStatement, setFinancialAidStatement] = useState<string>('');
  const [financialAidTitle, setFinancialAidTitle] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const onSubmit = React.useCallback(async (values: any, form: FormApi) => {
    try {
      setShowAlert(false);
      const authToken = requiresInternalAuthentication(values[RELATIONSHIP_TO_PATIENT_NAME_V4])
        ? await getOktaAccessToken()
        : undefined;
      if (props.validatedValues) {
        setReadOnlyValidatedValuesOnSubmit(values, patientInformationV4Fields, props.validatedValues);
      }
      deleteUnmetConditionalFields(values, getAllFinancialAssistanceV4TableFormFields());
      deleteFalsyStringFields(values);

      const { statement, status, title } = await submitFormGeneric<
        FinancialAidDto<FinancialAssistanceCalculationStatus>
      >(FINANCIAL_ASSISTANCE_V4_KEY, selectedId, values, authToken);

      if (props.handleNavigationSubmit) {
        const badgeValues: StatusBadgeProps = {
          text: statement,
          title: title,
          type: status,
        };
        props.handleNavigationSubmit(FINANCIAL_ASSISTANCE_V4_KEY, badgeValues);
      } else {
        setFinancialAidStatus(status);
        setFinancialAidStatement(statement);
        setFinancialAidTitle(title);
        setShowAlert(true);
        form.restart();
      }
    } catch (e) {
      setShowAlert(false);
      return { [FORM_ERROR]: 'Submission Failed' };
    }
  }, []);

  return (
    <PatientFormsTitleInjection>
      <FAOncoNeuroSelector {...props} formKey={FINANCIAL_ASSISTANCE_V4_KEY}>
        <Form onSubmit={onSubmit}>
          <FinancialAssistanceV4
            showAlert={showAlert}
            setShowAlert={setShowAlert}
            title={financialAidTitle}
            statement={financialAidStatement}
            status={financialAidStatus}
            {...props}
          />
        </Form>
      </FAOncoNeuroSelector>
    </PatientFormsTitleInjection>
  );
};

export default FinancialAssistanceV4Wrapper;
